import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faSpinner, faImage, faRobot, faFilter, faXmark } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import CardCheckbox from '@/components/ui/CardCheckbox';
import MarkdownEditor from '@/components/ui/MarkdownEditor.tsx';
import ThumbnailEditModal from '@/components/ui/ThumbnailEditModal.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showErrorToast } from '@/utils/toasts.ts';
import type { Module, CourseID } from '@/models';
import type { UpdateModuleRequest } from '@/modelsAPI';

interface EditModuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: CourseID;
  module: Module | null;
  onModuleUpdated: (module: Module) => void;
}

export default function EditModuleModal({
  isOpen,
  onClose,
  courseId,
  module,
  onModuleUpdated
}: EditModuleModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    order: 0,
    noKnowledge: false,
    limitVideosByCount: null as number | null,
    limitVideosByDays: null as number | null
  });
  const [loading, setLoading] = useState(false);
  const [isThumbnailEditOpen, setIsThumbnailEditOpen] = useState(false);

  useEffect(() => {
    if (module) {
      setFormData({
        title: module.title,
        description: module.description,
        order: module.order,
        noKnowledge: module.noKnowledge,
        limitVideosByCount: module.limitVideosByCount,
        limitVideosByDays: module.limitVideosByDate !== null ? Math.round(module.limitVideosByDate / 86400) : null
      });
    }
  }, [module]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!module) return;

    // Validation
    if (!formData.title.trim()) {
      showErrorToast('Bitte geben Sie einen Titel ein');
      return;
    }


    try {
      setLoading(true);

      const updateData: UpdateModuleRequest = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        order: formData.order,
        noKnowledge: formData.noKnowledge,
        limitVideosByCount: formData.limitVideosByCount,
        limitVideosByDate: formData.limitVideosByDays !== null ? formData.limitVideosByDays * 86400 : null
      };

      const response = await APIRoutes.updateModule(module.id, updateData);

      console.log('EditModuleModal - API response:', response);
      onModuleUpdated(response);
      onClose();
    } catch (err) {
      console.error('Error updating module:', err);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Modul bearbeiten"
      icon={<FontAwesomeIcon icon={faBook} className="w-5 h-5 text-blue-600" />}
      closeDisabled={loading}
      maxWidth="lg"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Titel *
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="z.B. Grundlagen der Chartanalyse"
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
            maxLength={200}
            required
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Beschreibung
          </label>
          <MarkdownEditor
            value={formData.description}
            onChange={(value) => setFormData(prev => ({ ...prev, description: value ?? '' }))}
            placeholder="Eine detaillierte Beschreibung des Moduls... (Markdown unterstützt)"
            disabled={loading}
            height={200}
          />
        </div>

        {/* Thumbnail Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Thumbnail
          </label>
          <div className="flex items-center space-x-4">
            {module?.thumbnail && (
              <div className="w-16 h-16 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                <img
                  src={APIRoutes.getBlobContentUrl(module.thumbnail)}
                  alt="Module thumbnail"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => setIsThumbnailEditOpen(true)}
              disabled={loading}
              className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/30"
            >
              <FontAwesomeIcon icon={faImage} className="w-4 h-4 mr-2" />
              {module?.thumbnail ? 'Thumbnail ändern' : 'Thumbnail hinzufügen'}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Reihenfolge
          </label>
          <input
            type="number"
            id="order"
            value={formData.order}
            onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
            disabled={loading}
            min={0}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
          />
        </div>

        {/* KI-Einstellungen */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            KI-Einstellungen
          </label>
          <div className="space-y-3">
            <CardCheckbox
              checked={formData.noKnowledge}
              onChange={(checked) => setFormData(prev => ({ ...prev, noKnowledge: checked }))}
              disabled={loading}
              icon={<FontAwesomeIcon icon={faRobot} className="w-4 h-4 text-gray-600 dark:text-gray-400 mr-2" />}
              label="Keine KI-Analyse"
              description="Videos in diesem Modul werden nicht analysiert und für den KI Chat verwendet"
            />
          </div>
        </div>

        {/* Video-Begrenzung */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
            Video-Begrenzung
          </label>
          <div className="space-y-4">
            <div>
              <label htmlFor="limitVideosByCount" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Maximale Anzahl sichtbarer Videos
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  id="limitVideosByCount"
                  value={formData.limitVideosByCount ?? ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    limitVideosByCount: e.target.value === '' ? null : parseInt(e.target.value)
                  }))}
                  placeholder="Kein Limit"
                  disabled={loading}
                  min={1}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                />
                {formData.limitVideosByCount !== null && (
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, limitVideosByCount: null }))}
                    disabled={loading}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
                    title="Limit entfernen"
                  >
                    <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div>
              <label htmlFor="limitVideosByDays" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Videos der letzten X Tage anzeigen
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  id="limitVideosByDays"
                  value={formData.limitVideosByDays ?? ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    limitVideosByDays: e.target.value === '' ? null : parseInt(e.target.value)
                  }))}
                  placeholder="Kein Limit"
                  disabled={loading}
                  min={1}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                />
                {formData.limitVideosByDays !== null && (
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, limitVideosByDays: null }))}
                    disabled={loading}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
                    title="Limit entfernen"
                  >
                    <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Ältere Videos (nach Datum oder Reihenfolge) werden ausgeblendet
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                Speichere...
              </>
            ) : (
              'Änderungen speichern'
            )}
          </button>
        </div>
      </form>

      <ThumbnailEditModal
        isOpen={isThumbnailEditOpen}
        onClose={() => setIsThumbnailEditOpen(false)}
        onThumbnailUpdated={() => {
          // Trigger a refresh of the module data if needed
        }}
        type="module"
        courseId={courseId}
        moduleId={module?.id}
        currentThumbnailUrl={module?.thumbnail ? APIRoutes.getBlobContentUrl(module.thumbnail) : undefined}
      />
    </Modal>
  );
}
